import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Headset, X } from 'lucide-react';
import { Modal } from '@/components/ui/drawer';
import { useAuth } from '@/lib/auth';
import { useSupport, type SupportMode } from '@/lib/support-context';
import { SupportForm, resolveSupportMode } from '@/components/support-form';

const HINT_COPY: Record<'import_help', { title: string; body: string }> = {
  import_help: {
    title: 'Need help importing?',
    body: "Send us your spreadsheet — we'll import it for you. Tap Help below.",
  },
};

export function SupportWidget() {
  const { user } = useAuth();
  const { hint, registerOpenHandler } = useSupport();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<string | undefined>();
  const [mode, setMode] = useState<SupportMode>('help');
  const [initialMessage, setInitialMessage] = useState('');
  // Bumped on each open so the form remounts with fresh state.
  const [session, setSession] = useState(0);
  const [dismissedHint, setDismissedHint] = useState(false);

  const loggedIn = Boolean(user?.user);

  useEffect(() => {
    registerOpenHandler((ctx, options) => {
      setContext(ctx);
      setMode(resolveSupportMode(ctx, options?.mode));
      setInitialMessage(options?.message ?? '');
      setSession((n) => n + 1);
      setOpen(true);
    });
  }, [registerOpenHandler]);

  const showHint = hint === 'import_help' && !dismissedHint && !open;
  const hintCopy = hint ? HINT_COPY[hint] : null;
  const isIdea = mode === 'idea';
  // The inline form on /contact replaces the floating button there.
  const showFab = pathname !== '/contact';

  return (
    <>
      {showHint && hintCopy && (
        <div className="fixed bottom-24 right-4 z-40 max-w-[260px] animate-fade-in max-sm:right-3 max-sm:max-w-[calc(100vw-1.5rem)]">
          <div className="relative rounded-xl border border-brand/25 bg-bg-surface p-3.5 shadow-card">
            <button
              type="button"
              aria-label="Dismiss tip"
              onClick={() => setDismissedHint(true)}
              className="absolute right-2 top-2 rounded-md p-1 text-text-muted hover:bg-bg-subtle hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <p className="pr-6 text-sm font-medium text-text-primary">{hintCopy.title}</p>
            <p className="mt-1 text-[13px] leading-snug text-text-secondary">{hintCopy.body}</p>
            <div className="absolute -bottom-2 right-8 h-4 w-4 rotate-45 border-b border-r border-brand/25 bg-bg-surface" />
          </div>
        </div>
      )}

      {showFab && (
      <button
        type="button"
        onClick={() => {
          setContext(hint === 'import_help' ? 'import_help' : undefined);
          setMode('help');
          setInitialMessage('');
          setSession((n) => n + 1);
          setOpen(true);
        }}
        className="fixed bottom-5 right-5 z-40 flex h-12 items-center justify-center gap-2 rounded-full bg-brand px-3.5 text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 max-sm:bottom-4 max-sm:right-4 max-sm:px-3"
        aria-label="Get help"
      >
        <Headset className="h-5 w-5 shrink-0" />
        <span className="hidden text-sm font-semibold min-[420px]:inline">Help</span>
      </button>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={isIdea ? 'Tell us what would help your shop' : 'Get help'}
        description={
          isIdea
            ? "We build and adjust features based on what shop owners ask for. We'll reply to your email."
            : loggedIn
              ? `We'll reply to ${user?.user.email}.`
              : "Tell us how we can help — we'll get back by email."
        }
      >
        <SupportForm
          key={session}
          context={context}
          mode={mode}
          initialMessage={initialMessage}
          hint={hint}
          onModeChange={setMode}
          onClose={() => setOpen(false)}
        />
      </Modal>
    </>
  );
}
